import { useState, useEffect } from "react";
import api from "../api/axiosInstance";
import SuccessModal from "./SuccessModal";
import emailjs from "@emailjs/browser";

export default function ReservationForm({ selectedDate }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: selectedDate.toISOString().split("T")[0],
    time: "",
    guests: "",
    note: "", // ✅ new field
  });

  const [blockedTimes, setBlockedTimes] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [specialOpenDays, setSpecialOpenDays] = useState([]);
  const [guestError, setGuestError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [reservationDetails, setReservationDetails] = useState(null);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      date: selectedDate.toISOString().split("T")[0],
    }));
  }, [selectedDate]);

  useEffect(() => {
    const fetchBlockedTimes = async () => {
      try {
        const [blockedRes, specialRes] = await Promise.all([
          api.get("/admin/public-blocked-times"),
          api.get("/admin/public-special-open"),
        ]);

        const normalizeUTC = (d) => new Date(d).toISOString().split("T")[0];

        const fixedBlocked = blockedRes.data.map((b) => ({
          ...b,
          date: normalizeUTC(b.date),
        }));

        const fixedSpecial = specialRes.data.map((d) => ({
          ...d,
          date: normalizeUTC(d.date),
        }));

        setBlockedTimes(fixedBlocked);
        setSpecialOpenDays(fixedSpecial);
      } catch (error) {
        console.error("Error fetching blocked/special days:", error);
      }
    };
    fetchBlockedTimes();
  }, []);

  useEffect(() => {
    if (!form.date) return;

    const selected = new Date(`${form.date}T00:00:00`);
    const day = selected.getDay();
    const isSpecialOpen = specialOpenDays.some((d) => d.date === form.date);
    const isMonday = day === 1;

    if (isMonday && !isSpecialOpen) {
      setForm((prev) => ({ ...prev, date: "" }));
      setAvailableTimes([]);
      return;
    }

    const times = [];
    for (let hour = 8; hour <= 15; hour++) {
      times.push(`${hour.toString().padStart(2, "0")}:00`);
      times.push(`${hour.toString().padStart(2, "0")}:15`);
      times.push(`${hour.toString().padStart(2, "0")}:30`);
      times.push(`${hour.toString().padStart(2, "0")}:45`);
    }
    times.push("16:00");

    const blockedForDate = blockedTimes.filter((b) => b.date === form.date);

    const toMinutes = (timeStr) => {
      const [h, m] = timeStr.split(":").map(Number);
      return h * 60 + m;
    };

    const timeStatus = times.map((t) => {
      const tMin = toMinutes(t);
      const isBlocked = blockedForDate.some((b) => {
        const startMin = toMinutes(b.startTime);
        const endMin = toMinutes(b.endTime);
        return tMin >= startMin && tMin < endMin;
      });
      return { time: t, blocked: isBlocked };
    });

    setAvailableTimes(timeStatus);

    if (timeStatus.some((t) => t.blocked && t.time === form.time)) {
      setForm((prev) => ({ ...prev, time: "" }));
    }
  }, [form.date, blockedTimes, specialOpenDays]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (form.guests > 8) {
    setGuestError("Maximum number of guests is 8.");
    return;
  }

  try {
    // 1️⃣ Save reservation in your backend
    await api.post("/reservations", form);

    // 2️⃣ Send thank-you email to customer
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID, // replace with your Service ID
      process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE_ID, // replace with customer template ID
      {
        name: form.name,
        email: form.email,
        date: form.date,
        time: form.time,
        guests: form.guests,
        note: form.note,
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY // replace with your Public Key
    );

    // 3️⃣ Send notification email to yourself
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID, // same service ID
      process.env.REACT_APP_EMAILJS_OWNER_TEMPLATE_ID, // owner template ID
      {
        name: form.name,
        email: form.email,
        phone: form.phone,
        date: form.date,
        time: form.time,
        guests: form.guests,
        note: form.note,
      },
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY
    );

    // 4️⃣ Show modal success
    setReservationDetails(form);
    setShowModal(true);
    setForm({
      name: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      guests: "",
      note: "", // ✅ reset
    });
    setGuestError("");

  } catch (error) {
    console.error("Error submitting reservation:", error);
    alert("Failed to submit reservation. Please try again.");
  }
};
  

  const handleDateChange = (e) => {
    setForm((prev) => ({ ...prev, date: e.target.value }));
  };

  const handleGuestChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (value > 8) {
      setGuestError("Maximum number of guests is 8.");
    } else {
      setGuestError("");
    }
    setForm((prev) => ({ ...prev, guests: e.target.value }));
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <h2>Reserve a Table</h2>

        <input
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />

        <input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />

        <input
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          required
        />

        <input
          type="date"
          value={form.date}
          onChange={handleDateChange}
          min={new Date().toISOString().split("T")[0]}
          max={new Date(new Date().setMonth(new Date().getMonth() + 1))
            .toISOString()
            .split("T")[0]}
          required
        />


        <select
          value={form.time}
          onChange={(e) => setForm({ ...form, time: e.target.value })}
          required
        >
          <option value="">Select Time</option>
          {availableTimes.map(({ time, blocked }) => (
            <option
              key={time}
              value={blocked ? "" : time}
              disabled={blocked}
              className={blocked ? "blocked-time" : ""}
              title={blocked ? "This time is unavailable" : ""}
            >
              {blocked ? `${time} - Full` : time}
            </option>
          ))}
        </select>

        <input
          type="number"
          placeholder="Number of Guests"
          value={form.guests}
          min="1"
          max="8"
          onChange={handleGuestChange}
          required
        />

        {guestError && (
          <p style={{ color: "red", fontSize: "0.9rem", marginTop: "-8px" }}>
            {guestError}
          </p>
        )}

        <textarea
          placeholder="Add a note (optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />

        <button type="submit">Reserve</button>
      </form>

      <SuccessModal
        show={showModal}
        onClose={() => setShowModal(false)}
        reservation={reservationDetails}
      />
    </>
  );
}
